<template>
  <div class="font-sans h-screen w-screen">
    <!-- Écran de sélection de boutique -->
    <div v-if="!shopSelected" class="h-full flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
      <div class="bg-white rounded-2xl shadow-2xl p-12 max-w-4xl w-full mx-8">
        <div class="text-center mb-12">
          <h1 class="text-5xl font-bold text-gray-800 mb-4">Commandes Lafitau</h1>
          <p class="text-2xl text-gray-600">Sélectionnez votre boutique</p>
        </div>
        
        <div class="grid grid-cols-2 gap-6">
          <button
            v-for="shop in shops"
            :key="shop.value"
            @click="shop.enabled && selectShop(shop.value)"
            :disabled="!shop.enabled"
            :class="[
              'font-bold py-8 px-6 rounded-xl text-2xl transition duration-200 shadow-lg',
              shop.enabled 
                ? 'bg-blue-600 hover:bg-blue-700 text-white transform hover:scale-105 cursor-pointer' 
                : 'bg-gray-300 text-gray-500 cursor-not-allowed opacity-60'
            ]"
          >
            {{ shop.label }}
            <span v-if="!shop.enabled" class="block text-sm mt-2">(Bientôt disponible)</span>
          </button>
        </div>
        
        <div class="mt-8 text-center">
          <button
            @click="selectShop('*')"
            class="bg-gray-600 hover:bg-gray-700 text-white font-semibold py-4 px-8 rounded-lg text-xl transition duration-200"
          >
            Toutes les boutiques
          </button>
        </div>
      </div>
    </div>
    
    <!-- Interface principale -->
    <div v-else class="h-full">
      <section
        class="grid grid-cols-[1fr_3fr_8cm] h-full"
      >
        <!-- Colonne de gauche -->
        <div class="flex flex-col h-full border-r">
          <!-- Encart boutique -->
          <div 
            @click="changeShop"
            class="bg-blue-600 text-white py-3 px-4 flex items-center gap-2 cursor-pointer hover:bg-blue-700 transition-colors"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path>
            </svg>
            <div class="flex-1">
              <div class="text-xs opacity-80">Boutique</div>
              <div class="text-sm font-bold">{{ selectedShopLabel }}</div>
            </div>
          </div>
          
          <!-- Barre de recherche -->
          <div class="relative bg-gray-50 border-b">
            <input
              v-model="searchQuery"
              type="text"
              placeholder="Rechercher une commande..."
              class="w-full pl-10 pr-4 py-2.5 text-sm focus:outline-none bg-white"
            />
            <div class="absolute inset-y-0 left-3 flex items-center pointer-events-none">
              <svg class="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M12.9 14.32a8 8 0 111.414-1.414l4.243 4.243-1.414 1.414-4.243-4.243zM8 14a6 6 0 100-12 6 6 0 000 12z" clip-rule="evenodd"></path>
              </svg>
            </div>
          </div>
          
          <!-- Liste des commandes -->
          <div class="bg-white overflow-auto flex-1 scrollbar-hide">
          <!-- Squelette de chargement -->
          <div v-if="loading" class="animate-pulse">
            <div class="p-4 bg-gray-100">
              <div class="h-6 bg-gray-200 rounded w-1/2"></div>
            </div>
            <ul>
              <li v-for="n in 5" :key="n" class="border-b p-4">
                <div class="flex justify-between items-center">
                  <div>
                    <div class="h-4 bg-gray-200 rounded w-32 mb-2"></div>
                    <div class="h-3 bg-gray-200 rounded w-24"></div>
                  </div>
                  <div class="text-right">
                    <div class="h-4 bg-gray-200 rounded w-16 mb-2"></div>
                    <div class="h-3 bg-gray-200 rounded w-20"></div>
                  </div>
                </div>
              </li>
            </ul>
          </div>
          <!-- Contenu réel -->
          <div v-else>
            <div
              v-if="filteredOrders.length === 0"
              class="p-4 text-center text-gray-600"
            >
              Aucune commande trouvée
            </div>
          <!-- Remplacez la liste des commandes par une boucle sur les groupes -->
          <ul class="max-h-[80vh] overflow-auto" v-else>
            <template v-for="(orders, status) in groupedOrders" :key="status">
              <div v-if="orders.length > 0">
                <h3
                  :class="[
                    'text-md font-semibold px-4 py-2', 
                    statusBadge[status]?.bgClass, 
                    statusBadge[status]?.textClass
                  ]"
                >
                {{ statusBadge[status]?.label || status }}
              </h3>
                <ul>
                  <li
                    v-for="orderItem in orders"
                    :key="orderItem.id"
                    @click="selectOrder(orderItem)"
                    :class="{ 'bg-gray-50': order && order.id === orderItem.id }"
                    class="border-b cursor-pointer hover:bg-gray-200 transition-colors"
                  >
                    <!-- Contenu de chaque commande -->
                    <div class="p-4 flex justify-between items-center gap-x-2 md:flex-col md:items-start">
                      <div>
                        <h3 class="font-bold text-gray-800 flex items-center md:items-start md:gap-2">
                          <span class="text-sm">#{{ orderItem.id }}</span>
                          <span
                            v-if="statusBadge[orderItem.status]"
                            :class="[
                              'ml-2',
                              'md:ml-0',
                              statusBadge[orderItem.status].bgClass,
                              statusBadge[orderItem.status].textClass,
                              'text-xs',
                              'font-medium',
                              'px-2',
                              'py-1',
                              'rounded-full',
                            ]"
                          >
                            {{ statusBadge[orderItem.status].label }}
                          </span>
                          <span
                            v-else
                            class="ml-2 bg-gray-200 text-gray-800 text-xs font-medium px-2 py-1 rounded-full"
                          >
                            {{ orderItem.status }}
                          </span>
                        </h3>

                        <p class="text-xs text-gray-600">
                          <span lass="font-semibold text-gray-800">Créée :</span> {{ formatDateorder(orderItem.date_created) }}
                        </p>
                        <p class="text-xs text-gray-600">
                          <span lass="font-semibold text-gray-800">Retrait :</span> {{ formatDateorder(orderItem.pickup.pickup_datetime) }}
                        </p>
                      </div>
                      <div class="flex gap-2 text-right md:text-left text-sm w-full">
                        <p class="text-gray-600 w-fit">Total :</p>
                        <p class="font-semibold text-gray-800">
                          {{ orderItem.total }}{{ orderItem.currency_symbol }}
                        </p>
                      </div>
                    </div>
                  </li>
                </ul>
              </div>
            </template>
          </ul>
          </div>
          </div>
          
          <!-- Checkbox archivées en bas -->
          <div class="bg-gray-50 px-4 py-3 border-t">
            <label class="flex items-center space-x-2 cursor-pointer">
              <input type="checkbox" v-model="isArchived" class="cursor-pointer" />
              <span class="text-xs">Commandes archivées</span>
            </label>
          </div>
        </div>

        <!-- Colonne de droite -->
        <div
          class="flex flex-col gap-6 bg-white overflow-auto h-full max-h-[calc(100vh-6rem)] scrollbar-hide pt-4 px-6"
        >
          <!-- Squelette de chargement -->
          <div v-if="loading" class="animate-pulse">
            <!-- En-tête de la commande -->
            <div class="flex justify-between">
              <div>
                <div class="flex gap-4 items-center mb-2">
                  <div class="h-6 bg-gray-200 rounded w-48"></div>
                  <div class="h-6 bg-gray-200 rounded w-16"></div>
                </div>
                <div class="h-4 bg-gray-200 rounded w-56"></div>
              </div>
              <div class="h-16 bg-gray-200 rounded w-48 mb-2"></div>
            </div>

            <!-- Sections du contenu -->
            <div class="space-y-4 mt-4">
              <!-- Squelette pour les articles -->
              <div>
                <div class="h-5 bg-gray-200 rounded w-56 mb-4"></div>
                <div v-for="n in 2" :key="n" class="flex items-center mb-4">
                  <div class="w-16 h-16 bg-gray-200 rounded-lg mr-4"></div>
                  <div class="flex-1 space-y-2">
                    <div class="h-4 bg-gray-200 rounded w-3/4"></div>
                    <div class="h-3 bg-gray-200 rounded w-1/2"></div>
                    <div class="h-3 bg-gray-200 rounded w-1/4"></div>
                  </div>
                </div>
              </div>

              <!-- Squelette pour le résumé de la commande -->
              <div class="border-t pt-4">
                <div class="h-5 bg-gray-200 rounded w-56 mb-4"></div>
                <div class="space-y-2">
                  <div v-for="n in 4" :key="n" class="flex justify-between">
                    <div class="h-4 bg-gray-200 rounded w-1/3"></div>
                    <div class="h-4 bg-gray-200 rounded w-1/4"></div>
                  </div>
                </div>
              </div>

              <!-- Squelette pour les informations de la commande  -->
              <div class="border-t pt-4">
                <div class="h-5 bg-gray-200 rounded w-56 mb-4"></div>
                <div class="space-y-2">
                  <div v-for="n in 4" :key="n" class="flex justify-between">
                    <div class="flex gap-4">
                      <div class="h-4 bg-gray-200 rounded w-16"></div>
                      <div class="h-4 bg-gray-200 rounded w-56"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div
            v-if="order"
            class="flex flex-col gap-6 bg-white overflow-auto h-full scrollbar-hide px-6 pt-6"
          >
            <!-- En-tête de la commande -->
            <div class="flex justify-between">
              <!-- Informations de la commande -->
              <div>
                <div class="flex gap-2 xl:gap-4 items-center flex-wrap">
                  <h2 class="text-2xl font-semibold text-gray-800">
                    Commande #{{ order.id }}
                  </h2>
                  <div v-if="order.status === 'processing'">
                    <span
                      class="bg-blue-main text-blue-accent text-lg font-semibold px-4 py-1 rounded-full"
                      >Payée</span
                    >
                  </div>

                  <div v-if="order.status === 'in-preparation'">
                    <span
                      class="bg-orange-main text-orange-accent text-lg font-semibold px-4 py-1 rounded-full"
                      >En préparation</span
                    >
                  </div>

                  <div v-if="order.status === 'ready-for-pickup'">
                    <span
                      class="bg-green-main text-green-accent text-lg font-semibold px-4 py-1 rounded-full"
                      >A livrer</span
                    >
                  </div>

                  <div v-if="order.status === 'completed'">
                    <span
                      class="bg-gray-1 text-gray-accent text-lg font-semibold px-4 py-1 rounded-full"
                      >Terminée</span
                    >
                  </div>
                </div>
                <p class="text-sm text-gray-600">
                  Effectuée le {{ formatDate(order.date_created) }}
                </p>
                <p class="text-xs text-gray-500" v-if="order.pickup && order.pickup.pickup_location_name">
                  Boutique de retrait : {{ order.pickup.pickup_location_name }}
                </p>
              </div>
              <!-- Bouton d'action -->
              <!-- sendOrderToStatus('in-preparation'); -->
              <button
                v-if="order.status === 'processing'"
                @click="generatePDF();sendOrderToStatus('in-preparation')"
                :disabled="isUpdatingStatus"
                class="flex items-center gap-2 w-fit text-white text-sm rounded-sm py-2 px-3 xl:py-4 xl:px-6 transition-all bg-blue-accent hover:bg-blue-main hover:text-white"
              >
                <span v-if="!isUpdatingStatus">Envoyer en préparation</span>
                <span v-else>Envoi...</span>
              </button>

              <button
                v-if="order.status === 'in-preparation'"
                @click="sendOrderToStatus('ready-for-pickup')"
                :disabled="isUpdatingStatus"
                class="flex items-center gap-2 w-fit text-white text-sm rounded-sm py-2 px-3 xl:py-4 xl:px-6 transition-all bg-orange-accent hover:bg-orange-main hover:text-white"
              >
                <span v-if="!isUpdatingStatus">Prête pour la livraison</span>
                <span v-else>Envoi...</span>
              </button>

              <button
                v-if="order.status === 'ready-for-pickup'"
                @click="sendOrderToStatus('completed')"
                :disabled="isUpdatingStatus"
                class="flex items-center gap-2 w-fit text-white text-sm rounded-sm py-2 px-3 xl:py-4 xl:px-6 transition-all bg-green-accent hover:bg-green-main hover:text-white"
              >
                <span v-if="!isUpdatingStatus">Commande récupérée</span>
                <span v-else>Envoi...</span>
              </button>

              <button
                v-if="order.status === 'in-preparation' || order.status === 'ready-for-pickup' || order.status === 'completed'"
                @click="generatePDF()"
                :disabled="isUpdatingStatus"
                class="flex items-center gap-2 w-fit text-black text-sm rounded-sm py-2 px-3 xl:py-4 xl:px-6 transition-all bg-gray-1 hover:bg-gray-1 hover:text-black"
              >
                <span v-if="!isUpdatingStatus">Duplicata</span>
                <span v-else>Duplicata...</span>
              </button>
            </div>

            <!-- Articles de la commande -->
            <div>
              <h3 class="text-lg font-semibold text-gray-800 mb-4">
                Articles de la commande
              </h3>

              <!-- En-tête du tableau -->
              <div
                class="flex bg-gray-100 p-2 font-semibold text-gray-800 text-sm"
              >
                <div class="w-2/5">Article</div>
                <div class="w-1/5 text-right">Prix</div>
                <div class="w-1/5 text-right">Quantité</div>
                <div class="w-1/5 text-right">Total</div>
                <div class="w-1/5 text-right">TVA</div>
              </div>

              <!-- Corps du tableau -->
              <div>
                <div
                  v-for="item in order.line_items"
                  :key="item.id"
                  class="flex flex-col md:flex-row items-center p-2 text-sm"
                >
                  <!-- Colonne Article -->
                  <div class="w-full md:w-2/5 flex items-center">
                    <img
                      :src="getProductImage(item)"
                      alt="Product image"
                      class="w-10 h-10 object-cover rounded-lg mr-4"
                    />
                    <p class="font-semibold text-gray-800">{{ item.name }}</p>
                  </div>

                  <!-- Colonne Prix -->
                  <div class="w-full md:w-1/5 text-right">
                    <span class="font-semibold">Prix : </span>
                    {{ formatPrice(getItemPriceWithTax(item)) }}
                  </div>

                  <!-- Colonne Quantité -->
                  <div class="w-full md:w-1/5 text-right">
                    <span class="font-semibold">Quantité : </span>
                    {{ item.quantity }}
                  </div>

                  <!-- Colonne Total -->
                  <div class="w-full md:w-1/5 text-right">
                    <span class="font-semibold">Total : </span>
                    {{ formatPrice(getItemTotal(item)) }}
                  </div>

                  <!-- Colonne TVA -->
                  <div class="w-full md:w-1/5 text-right">
                    <span class="font-semibold">TVA : </span>
                    {{ formatPrice(getItemTax(item)) }}
                  </div>
                </div>
              </div>
            </div>

            <!-- Résumé de la commande -->
            <div class="border-t pt-4">
              <h3 class="text-lg font-semibold text-gray-800 mb-4">
                Résumé de la commande
              </h3>
              <div class="space-y-2 text-sm">
                <!-- Sous-total -->
                <div class="flex justify-between">
                  <span class="text-gray-600">Sous-total</span>
                  <span class="text-gray-800">{{
                    formatPrice(
                      parseFloat(order.total) +
                        parseFloat(order.discount_total) -
                        parseFloat(order.total_tax)
                    )
                  }}</span>
                </div>

                <!-- Remises -->
                <div
                  v-if="order.discount_total > 0"
                  class="flex justify-between"
                >
                  <span class="text-gray-600">Remise</span>
                  <span class="text-gray-800"
                    >-{{ formatPrice(order.discount_total) }}</span
                  >
                </div>

                <!-- Coupons appliqués -->
                <div
                  v-for="coupon in order.coupon_lines"
                  :key="coupon.id"
                  class="flex justify-between"
                >
                  <span class="text-gray-600">Coupon ({{ coupon.code }})</span>
                  <span class="text-gray-800"
                    >-{{ formatPrice(coupon.discount) }}</span
                  >
                </div>

                <!-- Frais de livraison -->
                <div
                  v-for="shipping in order.shipping_lines"
                  :key="shipping.id"
                  class="flex justify-between"
                >
                  <span class="text-gray-600">Frais de livraison</span>
                  <span class="text-gray-800">{{
                    formatPrice(shipping.total)
                  }}</span>
                </div>

                <!-- Taxes -->
                <div
                  v-for="tax in order.tax_lines"
                  :key="tax.id"
                  class="flex justify-between"
                >
                  <span class="text-gray-600">{{ tax.label }}</span>
                  <span class="text-gray-800">{{
                    formatPrice(tax.tax_total)
                  }}</span>
                </div>

                <!-- Total -->
                <div class="flex justify-between font-semibold pt-2">
                  <span>Total</span>
                  <span>{{ formatPrice(order.total) }}</span>
                </div>
              </div>
            </div>

            <!-- Note du client -->
            <div v-if="order.customer_note" class="border-t pt-4">
              <h3 class="text-lg font-semibold text-gray-800 mb-4">
                Note du client
              </h3>
              <p class="text-gray-600 text-sm">{{ order.customer_note }}</p>
            </div>

            <!-- Informations du client -->
            <div class="border-t pt-4">
              <h3 class="text-lg font-semibold text-gray-800 mb-4">
                Informations du client
              </h3>
              <div class="space-y-2 text-sm">
                <p>
                  <span class="font-semibold">Nom :</span>
                  {{ order.billing.first_name }} {{ order.billing.last_name }}
                </p>
                <p>
                  <span class="font-semibold">Email :</span>
                  {{ order.billing.email }}
                </p>
                <p>
                  <span class="font-semibold">Téléphone :</span>
                  {{ order.billing.phone || "Non fourni" }}
                </p>
              </div>
            </div>

            <!-- Adresse de facturation -->
            <!--
                        <div class="border-t pt-4 mb-16">
                            <h3 class="text-lg font-semibold text-gray-800 mb-4">Adresse de facturation</h3>
                            <div class="space-y-1">
                                <p>{{ order.billing.address_1 }}</p>
                                <p>{{ order.billing.postcode }} {{ order.billing.city }}</p>
                                <p>{{ order.billing.country }}</p>
                            </div>
                        </div>
                    -->
          </div>
        </div>

        <vue3-html2pdf
          ref="html2Pdf"
          :show-layout="true"
          :float-layout="false"
          :enable-download="false"
          :preview-modal="false"
          :manual-pagination="false"
          filename="ticket.pdf"
          pdf-content-width="8cm"
          :paginate-elements-by-height="2480"
          @beforeDownload=handleBeforeDownload
        >
          <template v-slot:pdf-content>
            <div
              v-if="order"
              class="order-ticket"
            >
              <div class="flex flex-col bg-[#F7F7F6] p-4 h-full">
              <div class="text-center">
                <p class="text-lg font-semibold text-gray-800">
                  S.A.R.L LAFITAU FRERES
                </p>
                <p class="text-sm text-gray-800">31 AVENUE DREO</p>
                <p>83170 BRIGNOLES (FRANCE)</p>
                <p>Tél.: 04.94.69.22.83</p>
                <p class="text-xs">TVA Intracom FR49315271320</p>
                <p class="text-xs">Siret 31527132000014 - NAF 158C</p>
                <p class="text-md font-semibold text-gray-800">
                  {{ formatDateTicket(order.date_created) }}
                </p>
              </div>
              <div class="w-full h-px bg-gray-800 mt-2"></div>
              <div class="w-full h-px bg-gray-800 mt-px mb-2"></div>
              <p class="text-md font-semibold text-gray-800 text-center">
                Ticket N° {{ order.id }}
              </p>
              <div class="w-full h-px bg-gray-800 my-2"></div>
              <!-- Articles de la commande -->
              <div>
                <!-- En-tête du tableau -->
                <div class="flex font-semibold text-gray-800 text-xs">
                  <div class="w-1/6">Qté</div>
                  <div class="w-3/6">Désignation</div>
                  <div class="w-1/6">Total.TTC</div>
                  <div class="w-1/6 text-right">T</div>
                </div>

                <!-- Corps du tableau -->
                <div>
                  <div
                    v-for="item in order.line_items"
                    :key="item.id"
                    class="flex flex-row items-center text-xs"
                  >
                    <!-- Colonne Quantité -->
                    <div class="w-1/6">
                      {{ item.quantity }}
                    </div>

                    <!-- Colonne Article -->
                    <div class="w-3/6 flex items-center">
                      <p class="text-gray-800 uppercase">
                        {{ item.name }}
                      </p>
                    </div>

                    <!-- Colonne Total -->
                    <div class="w-1/6">
                      {{ formatPrice(getItemTotal(item)) }}
                    </div>

                    <!-- Colonne TVA -->
                    <div class="w-1/6 text-right">{{ getVATLetter( getItemTVAPercent(item) ) }}</div>
                  </div>
                </div>
                <div class="flex text-gray-800 text-xs">
                  <div class="w-1/6">TOTAL</div>

                  <div class="w-3/6">
                    ( {{ order.line_items.length }} Art. )
                  </div>

                  <div class="w-1/6 text-right">
                    {{ formatPrice(order.total) }}
                  </div>
                </div>
                <div class="flex text-gray-800 text-xs">
                  <div class="w-2/6 font-semibold underline">
                    Règlement :
                  </div>
                </div>
                <div class="flex text-gray-800 text-xs">
                  <div
                    class="w-3/6 md:translate-x-[66%] uppercase text-left"
                  >
                    {{ order.payment_method }}
                  </div>
                  <div class="w-3/6 md:translate-x-[33%] text-left">
                    {{ formatPrice(order.total) }}
                  </div>
                </div>
              </div>
              <div class="w-full h-px bg-gray-800 my-2"></div>
              <div>
                <!-- En-tête du tableau -->
                <div class="flex font-semibold text-gray-800 text-xs">
                  <div class="w-1/5">Code.T</div>
                  <div class="w-1/5">TVA</div>
                  <div class="w-1/5">Mt.HT</div>
                  <div class="w-1/5 text-right">Mt.TVA</div>
                  <div class="w-1/5 text-right">Mt.TTC</div>
                </div>

                <!-- Corps du tableau -->
                <div class="text-xs flex">
                  <div class="w-full md:w-1/5">{{ getVATLetter( getTVAPercent(order) ) }}</div>

                  <div class="w-full md:w-1/5 flex items-center">
                    <p class="text-gray-800 uppercase">
                      {{ getTVAPercent(order) }}%
                    </p>
                  </div>

                  <div class="w-full md:w-1/5">
                    {{
                      formatPriceWithoutCurrency(
                        parseFloat(order.total) +
                          parseFloat(order.discount_total) -
                          parseFloat(order.total_tax)
                      )
                    }}
                  </div>

                  <div class="w-full md:w-1/5 text-right">
                    {{ formatPriceWithoutCurrency(order.total_tax) }}
                  </div>
                  <div class="w-full md:w-1/5 text-right">
                    {{ formatPriceWithoutCurrency(order.total) }}
                  </div>
                </div>
                <div class="text-sm flex items-center">
                  <div
                    class="text-md font-semibold text-gray-800 w-full md:w-3/5 text-left"
                  >
                    Net à Payer
                  </div>

                  <div
                    class="text-lg font-semibold text-gray-800 text-right w-full md:w-2/5"
                  >
                    {{ order.total }} EUR
                  </div>
                </div>
              </div>
              <div class="w-full h-px bg-gray-800 my-2"></div>
              <p class="text-md text-gray-800">
                <span class="font-semibold">Boutique:</span> {{ order.pickup.pickup_location_name }}
              </p>
              <p class="text-md text-gray-800 font-extrabold">
                <span>Retrait:</span> {{ formatDate(order.pickup.pickup_datetime) }}
              </p>
              <div class="w-full h-px bg-gray-800 my-2"></div>
              <p class="text-xs text-gray-800">LAFITAU - COMMANDE INTERNET</p>
              <p class="text-xs text-gray-800">https://www.lafitau.fr</p>
            </div>
            </div>
          </template>
        </vue3-html2pdf>
      </section>
    </div>
  </div>
</template>

<script>
import Vue3Html2pdf from "vue3-html2pdf";
import * as pdfjsLib from "pdfjs-dist/webpack";
import io from "socket.io-client";

export default {
  el: "#app",
  components: { Vue3Html2pdf },

  data() {
    return {
      socket: null, // Instance Socket.IO
      shopSelected: false, // Indique si une boutique a été sélectionnée
      selectedShop: null, // Boutique sélectionnée
      showAllOrders: false, // Afficher toutes les commandes (y compris pending)
      shops: [
        { value: "Lafitau Saint-Maximin", label: "Lafitau Saint-Maximin", enabled: true },
        { value: "Lafitau Dréo", label: "Lafitau Dréo", enabled: true },
        { value: "Lafitau Caramy", label: "Lafitau Caramy", enabled: false },
        { value: "Lafitau Rocbaron", label: "Lafitau Rocbaron", enabled: true },
        { value: "Lafitau Garéoult", label: "Lafitau Garéoult", enabled: false },
        { value: "Lafitau Saint-Lazare", label: "Lafitau Saint-Lazare", enabled: true }
      ],
      orders: [], // Liste des commandes à gauche
      order: null, // Commande sélectionnée à droite
      loading: true,
      searchQuery: "", // Requête de recherche
      isUpdatingStatus: false,
      isArchived: false, // Par défaut, afficher les commandes du jour
      statusBadge: {
        "processing": {
          label: "Payée",
          bgClass: "bg-blue-main",
          textClass: "text-blue-accent",
        },
        "in-preparation": {
          label: "En préparation",
          bgClass: "bg-orange-main",
          textClass: "text-orange-accent",
        },
        "ready-for-pickup": {
          label: "A livrer",
          bgClass: "bg-green-main",
          textClass: "text-green-accent",
        },
        "completed": {
          label: "Terminée",
          bgClass: "bg-gray-1",
          textClass: "text-gray-accent",
        },
        "pending": {
          label: "En attente de paiement",
          bgClass: "bg-yellow-100",
          textClass: "text-yellow-800",
        },
        // Ajouter d'autres statuts si nécessaire
      },
    };
  },
  computed: {
    selectedShopLabel() {
      if (this.selectedShop === "*") {
        return "Toutes les boutiques";
      }
      return this.selectedShop;
    },
    pdfFilename() {
      return `ticket-${this.order?.id || "default"}.pdf`; // Fallback to 'default' if `order` or `order.id` is undefined
    },
    groupedOrders() {
      const groups = {
        "pending": [],
        "ready-for-pickup": [],
        "processing": [],
        "in-preparation": [],
        "completed": [],
        // Ajouter d'autres statuts si nécessaire
      };

      this.filteredOrders.forEach(order => {
        if (groups[order.status]) {
          groups[order.status].push(order);
        } else {
          // Si un statut inattendu est rencontré
          groups[order.status] = [order];
        }
      });

      return groups;
    },
    filteredOrders() {
        let filtered = this.orders;
        
        // Filtrer par boutique
        if (this.selectedShop !== "*") {
          filtered = filtered.filter((order) => {
            return order.pickup && order.pickup.pickup_location_name === this.selectedShop;
          });
        }
        
        // Filtrer par recherche
        if (this.searchQuery) {
          const query = this.searchQuery.toLowerCase();
          filtered = filtered.filter((order) => {
            const orderId = order.id.toString();
            const customerName = `${order.billing.first_name} ${order.billing.last_name}`.toLowerCase();
            const customerEmail = order.billing.email.toLowerCase();
            return (
              orderId.includes(query) ||
              customerName.includes(query) ||
              customerEmail.includes(query)
            );
          });
        }
        
        return filtered;
      },
  },

  created() {
    // Vérifier le paramètre URL pour afficher toutes les commandes
    const urlParams = new URLSearchParams(window.location.search);
    this.showAllOrders = urlParams.get('all-orders') === 'yes';
    
    if (this.showAllOrders) {
      console.log('🔓 Mode "Toutes les commandes" activé (y compris pending)');
    }
    
    // Charger la boutique sélectionnée depuis localStorage
    const savedShop = localStorage.getItem('selectedShop');
    if (savedShop) {
      this.selectedShop = savedShop;
      this.shopSelected = true;
      this.initializeApp();
    } else {
      // Titre initial pour l'écran de sélection
      document.title = 'Lafitau - Sélectionnez la boutique';
    }
  },
  methods: {
    selectShop(shop) {
      this.selectedShop = shop;
      this.shopSelected = true;
      localStorage.setItem('selectedShop', shop);
      // Réinitialiser la commande sélectionnée lors du changement de boutique
      this.order = null;
      this.initializeApp();
    },
    changeShop() {
      this.shopSelected = false;
      this.selectedShop = null;
      this.orders = [];
      this.order = null;
      localStorage.removeItem('selectedShop');
    },
    initializeApp() {
      // Configurer Socket.IO seulement si pas déjà connecté
      if (!this.socket || !this.socket.connected) {
        this.setupSocketIO();
      }

      // Récupérer les commandes au chargement
      this.fetchOrders();
    },
    setupSocketIO() {
      // Fermer la connexion existante si elle existe
      if (this.socket) {
        this.socket.off('orderModified');
        this.socket.disconnect();
      }

      // Créer une nouvelle connexion Socket.IO
      this.socket = io(process.env.VUE_APP_API_SERVER_URL, {
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        reconnectionAttempts: Infinity
      });

      this.socket.on("connect", () => {
        console.log("✅ Connecté au serveur Socket.IO", this.socket.id);
      });

      this.socket.on("disconnect", (reason) => {
        console.log("❌ Déconnecté de Socket.IO:", reason);
      });

      this.socket.on("connect_error", (error) => {
        console.error("⚠️ Erreur de connexion à Socket.IO :", error.message);
      });

      this.socket.on("reconnect", (attemptNumber) => {
        console.log("🔄 Reconnecté après", attemptNumber, "tentatives");
      });

      // Écouter l'événement 'orderModified'
      this.socket.on("orderModified", (orderData) => {
        console.log("📦 Nouvelle commande reçue via WebSocket:", orderData.id, orderData.status);

        if (orderData.status === "processing") {
          this.playNotificationSound();
        }
        
        // Définir les statuts valides selon le mode
        const validStatuses = this.showAllOrders 
          ? ["pending", "processing", "in-preparation", "ready-for-pickup", "completed"]
          : ["processing", "in-preparation", "ready-for-pickup", "completed"];
        
        if (validStatuses.includes(orderData.status)) {
          // Vérifier si la commande existe déjà dans la liste
          const existingOrderIndex = this.orders.findIndex(
            (o) => o.id === orderData.id
          );
          if (existingOrderIndex !== -1) {
            // Mettre à jour la commande existante
            console.log("🔄 Mise à jour de la commande existante #", orderData.id);
            this.orders.splice(existingOrderIndex, 1, orderData);
          } else {
            // Ajouter la nouvelle commande
            console.log("➕ Ajout d'une nouvelle commande #", orderData.id);
            this.orders.unshift(orderData);
          }
          if (this.order && this.order.id === orderData.id) {
            this.order = orderData;
          }
        } else {
          // Supprimer la commande si elle n'est plus en statut valide
          console.log("🗑️ Suppression de la commande #", orderData.id);
          this.orders = this.orders.filter((o) => o.id !== orderData.id);
        }
      });
    },
    playNotificationSound() {
      const audio = new Audio();
      // Max volume
      audio.volume = 1.0;
      audio.loop = false;
      audio.muted = true;
      audio.autoplay = false;
      audio.preload = "auto";
      audio.src = "/sound/notification.ogg";
      audio.addEventListener("canplaythrough", () => {
        audio.play();
      }, { once: true });
    },
    generatePDF() {
      this.$refs.html2Pdf.generatePdf();
    },
    async handleBeforeDownload({ html2pdf, options, pdfContent }) {
      console.log("handleBeforeDownload called");
      // Génère le PDF en blob

      options.jsPDF.format = [80, 297.04]
      options.jsPDF.unit = "mm";
      console.log(options);
      const pdfBlob = await html2pdf()
        .set(options)
        .from(pdfContent)
        .toPdf()
        .outputPdf("blob");
      const pdfUrl = URL.createObjectURL(pdfBlob);

      // Charge le PDF avec pdfjsLib
      const loadingTask = pdfjsLib.getDocument(pdfUrl);
      const pdfDoc = await loadingTask.promise;
      const canvasContainer = document.createElement("div"); // Conteneur temporaire

      const computeScale = (page) => {
        const defaultViewport = page.getViewport({ scale: 1 });
        const availableWidth = document.documentElement.clientWidth;
        const baseScale = availableWidth / defaultViewport.width;
        const printScaleFactor = 1; // Multiplie par 1.5 pour du texte 50% plus grand
        return baseScale * printScaleFactor;
      };

      // Fonction pour rendre une page dans un canvas avec un scale adapté
      const renderPage = async (pageNumber) => {
        const page = await pdfDoc.getPage(pageNumber);
        const scale = computeScale(page); // Calcul dynamique du scale
        console.log('scale: ', scale);
        const viewport = page.getViewport({ scale });

        const canvas = document.createElement("canvas");
        const context = canvas.getContext("2d");
        canvas.width = viewport.width;
        canvas.height = viewport.height;

        const renderContext = {
          canvasContext: context,
          viewport: viewport,
        };

        await page.render(renderContext).promise;
        // Ajoute le canvas au conteneur
        canvasContainer.appendChild(canvas);
      };

      // Rendu de toutes les pages du PDF
      for (let i = 1; i <= pdfDoc.numPages; i++) {
        await renderPage(i);
      }

      // Ajoute le conteneur au DOM pour que l'impression puisse l'afficher
      canvasContainer.id = "print-container";
      canvasContainer.style.display = "block"; // Cacher le conteneur
      document.body.appendChild(canvasContainer);

      // Focalise la fenêtre et lance l'impression
      window.focus();
      window.print();

      //canvasContainer.style.display = "none"; // Cacher le conteneur après impression

      // (Optionnel) Nettoyage après impression :
      //document.body.removeChild(canvasContainer);
    },
    getTVAPercent(order) {
      order.tax_lines.forEach((tax) => {
        if (tax.label === "TVA") {
          return tax.tax_percent || 5.5;
        }
      });
      return 5.5;
    },
    getVATLetter(vatRate) {
      switch (vatRate) {
        case 20:
          return "A";
        case 5.5:
          return "B";
        case 10:
          return "D";
        default:
          return "Unknown VAT rate";
      }
    },
    getItemTVAPercent(product) {
      const subtotal = parseFloat(product.subtotal);
      const tax = parseFloat(product.subtotal_tax);
      if (subtotal === 0) {
        return 0;
      }
      // Compute the VAT rate from the given values.
      const computedVAT = (tax / subtotal) * 100;
      
      // Allowed VAT rates.
      const allowedRates = [20, 5.5, 10];
      
      // Find the closest allowed rate.
      let closestRate = allowedRates[0];
      let smallestDiff = Math.abs(computedVAT - allowedRates[0]);
      
      allowedRates.forEach(rate => {
        const diff = Math.abs(computedVAT - rate);
        if (diff < smallestDiff) {
          smallestDiff = diff;
          closestRate = rate;
        }
      });
      
      return closestRate;
    },
    is48hoursCommand(dateStr, orderStatus) {
      if( orderStatus !== 'completed') return true;

      const today = new Date();
      const date = new Date(dateStr);
      const diffTime = Math.abs(today - date);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays < 2;
    },
    isToday(dateStr) {
      const today = new Date();
      const date = new Date(dateStr);
      return (
        date.getDate() === today.getDate() &&
        date.getMonth() === today.getMonth() &&
        date.getFullYear() === today.getFullYear()
      );
    },
    formatDateTicket(dateStr) {
      if (!dateStr) return "";

      const date = new Date(dateStr);

      const options = {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      };

      // Créer un formateur avec les options spécifiées
      const formatter = new Intl.DateTimeFormat("fr-FR", options);
      // Utiliser formatToParts pour obtenir les différentes parties de la date
      const parts = formatter.formatToParts(date);

      // Fonction pour capitaliser la première lettre
      const capitalize = (s) => s.charAt(0).toUpperCase() + s.slice(1);

      // Objets pour stocker les parties de la date
      let weekday, day, month, year, hour, minute;

      // Parcourir les parties et extraire les valeurs
      parts.forEach((part) => {
        switch (part.type) {
          case "weekday":
            weekday = capitalize(part.value);
            break;
          case "day":
            day = part.value;
            break;
          case "month":
            month = capitalize(part.value);
            break;
          case "year":
            year = part.value;
            break;
          case "hour":
            hour = part.value;
            break;
          case "minute":
            minute = part.value;
            break;
          // Vous pouvez gérer d'autres types si nécessaire
          default:
            break;
        }
      });

      // Construire la chaîne formatée
      return `${weekday} ${day} ${month} ${year} ${hour}:${minute}`;
    },

    sendOrderToStatus(status) {
      if (!this.order) return;

      this.isUpdatingStatus = true; // Activer l'état de chargement

      // Envoyer la requête au serveur pour mettre à jour le statut
      fetch(`${process.env.VUE_APP_API_SERVER_URL}/api/orders/${this.order.id}/status`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ status: status }),
      })
        .then((response) => {
          console.log(response);
          if (!response.ok) {
            throw new Error(`Erreur HTTP ! statut : ${response.status}`);
          }
          return response.json();
        })
        .then((updatedOrder) => {
          // Mettre à jour l'objet 'order' avec les nouvelles données
          this.order = updatedOrder;

          // Mettre à jour la commande dans la liste 'orders'
          const index = this.orders.findIndex((o) => o.id === updatedOrder.id);
          if (index !== -1) {
            this.orders.splice(index, 1, updatedOrder);
          }
        })
        .catch((error) => {
          console.error(
            "Erreur lors de la mise à jour du statut de la commande :",
            error
          );
        })
        .finally(() => {
          this.isUpdatingStatus = false; // Désactiver l'état de chargement
        });
    },
    formatDate(dateString) {
      if (!dateString) return "";
      const options = {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      };
      const date = new Date(dateString);
      return date.toLocaleDateString("fr-FR", options);
    },
    formatDateorder(dateString) {
      if (!dateString) return "";
      const options = {
        year: "numeric",
        month: "numeric",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      };
      const date = new Date(dateString);
      return date.toLocaleDateString("fr-FR", options);
    },
    getItemPriceWithTax(item) {
      // Prix unitaire avec TVA
      const price = parseFloat(item.price);
      const tax = parseFloat(item.total_tax) / item.quantity;
      return price + tax;
    },
    getItemTotal(item) {
      // Total = Prix unitaire avec TVA * Quantité
      const priceWithTax = this.getItemPriceWithTax(item);
      return priceWithTax * item.quantity;
    },
    getItemTax(item) {
      // TVA totale pour l'article
      return parseFloat(item.total_tax);
    },
    formatPriceWithoutCurrency(amount) {
      if (typeof amount === "undefined" || amount === null) return "";
      const number = parseFloat(amount);
      return number.toLocaleString("fr-FR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    formatPrice(amount) {
      if (typeof amount === "undefined" || amount === null) return "";
      const number = parseFloat(amount);
      return number.toLocaleString("fr-FR", {
        style: "currency",
        currency: this.order.currency,
        currencyDisplay: "code", // This will show "EUR" instead of the symbol
      });
    },
    getProductImage(item) {
      // Si l'image est incluse dans les données de l'article
      if (item.image && item.image.src) {
        return item.image.src;
      }
      // Sinon, retournez une image par défaut
      return "https://via.placeholder.com/150";
    },
    fetchOrders() {
      this.loading = true; // Commencer le chargement
      
      let endpoint = `${process.env.VUE_APP_API_SERVER_URL}/api/orders`;
      const params = [];
      
      if (this.isArchived) {
        params.push('archive=true');
      }
      if (this.showAllOrders) {
        params.push('all=true');
      }
      
      if (params.length > 0) {
        endpoint += '?' + params.join('&');
      }
      
      console.log('📡 Fetching orders from:', endpoint);

      fetch(endpoint)
        .then((response) => {
          if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
          }
          const res = response.json();
          console.log(res);
          return res;
        })
        .then((data) => {
          if (this.isArchived) {
            // Filtrer les commandes du dernier mois
            const oneMonthAgo = new Date();
            oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
            this.orders = data.filter(order => new Date(order.date_created) >= oneMonthAgo);
          } else {
          // Filtrer les commandes des dernières 48 heures
            this.orders = data.filter(order => this.is48hoursCommand(order.date_created, order.status));
          }
          if (this.orders.length > 0) {
            this.order = this.orders[0]; // Sélectionner la première commande par défaut
          } else {
            this.order = null; // Aucune commande à sélectionner
          }
          console.log(this.orders);
        })
        .catch((error) => {
          console.error("Erreur lors de la récupération des commandes :", error);
        })
        .finally(() => {
          console.log("Chargement terminé");
          this.loading = false; // Terminer le chargement
        });
    },
    selectOrder(orderItem) {
      // Vérifier si la commande appartient à la boutique sélectionnée
      if (this.selectedShop !== "*") {
        if (!orderItem.pickup || orderItem.pickup.pickup_location_name !== this.selectedShop) {
          // Ne pas afficher la commande si elle n'appartient pas à la boutique
          return;
        }
      }
      this.order = orderItem;
    },
  },
  watch: {
    shopSelected(selected) {
      if (selected) {
        // Mettre à jour le titre quand une boutique est sélectionnée
        document.title = `${this.selectedShopLabel}`;
      } else {
        // Titre pour l'écran de sélection
        document.title = 'Lafitau - Sélectionnez la boutique';
      }
    },
    selectedShop() {
      // Mettre à jour le titre quand on change de boutique
      if (this.shopSelected) {
        document.title = `Lafitau ${this.selectedShopLabel}`;
      }
    },
    isArchived() {
      this.fetchOrders();
    },
    filteredOrders(newOrders) {
      // Quand la liste filtrée change, vérifier si la commande actuelle est toujours valide
      if (newOrders.length > 0) {
        // Si la commande actuelle n'est pas dans la liste filtrée, réinitialiser
        if (!this.order || !newOrders.find(o => o.id === this.order.id)) {
          // Sélectionner la première commande SEULEMENT si elle appartient à la boutique
          const firstOrder = newOrders[0];
          if (this.selectedShop === "*" || 
              (firstOrder.pickup && firstOrder.pickup.pickup_location_name === this.selectedShop)) {
            this.order = firstOrder;
          } else {
            this.order = null;
          }
        }
      } else {
        // Aucune commande dans la liste filtrée, ne rien afficher
        this.order = null;
      }
    },
  },
  beforeUnmount() {
    // Nettoyer la connexion Socket.IO lors de la destruction du composant
    if (this.socket) {
      console.log("🔌 Déconnexion de Socket.IO");
      this.socket.off('orderModified');
      this.socket.disconnect();
      this.socket = null;
    }
  },
};
</script>
